/**
 * CUÁNTOS DECIMALES ENSEÑA UN ACTIVO CRIPTO — fuente única, ISOMÓRFICA.
 *
 * ⚠️⚠️ LA REGLA VIVÍA DOS VECES, Y LAS DOS COPIAS YA NO DECÍAN LO MISMO (#2234).
 *
 *   - El backend derivaba `min(bat.assets.decimals, 8)` en
 *     `asset-precision-registry.service.ts` y publicaba el resultado como
 *     `precision` en cada fila de `GET /api/balances`.
 *   - El frontend llevaba su propia tabla por ticker en
 *     `shared/constants/asset-precision.ts` — SOL 9, DOT 10, ADA 6 — que es la
 *     que pinta la rejilla de transacciones.
 *
 * Medido contra `bat_dev` el 2026-09-12, para el MISMO activo:
 *
 *     SOL → saldos 8 decimales · rejilla 9 decimales
 *     DOT → saldos 8 decimales · rejilla 10 decimales
 *
 * La unidad nativa de Solana es el lamport = 1e-9 SOL, así que un tope de 8
 * redondea a la DECENA de lamports: 10.886 filas de SOL de `bat_dev` traen un
 * noveno decimal significativo que la pantalla de saldos no podía enseñar. Y
 * contradice a #2211, que se implementó y se verificó en pantalla justamente
 * para que la rejilla mostrase los nueve.
 *
 * POR QUÉ LA REGLA ES UN UMBRAL Y NO UN TOPE. Un `min(decimals, N)` global
 * confunde dos cosas distintas: la precisión que la cadena ofrece y el ancho
 * que una celda puede leer. Los decimales de una cadena son su unidad nativa
 * —satoshi 8, lamport 9, planck 10, lovelace 6— y enseñarlos enteros nunca
 * inventa un dígito. El caso que el recorte existe para resolver es otro: los
 * 36 ERC-20 del catálogo declaran 18 decimales porque su unidad es el wei, y
 * nadie cuadra una cartera leyendo 18 dígitos. Así que la unidad nativa se
 * enseña ENTERA mientras quepa en un ancho legible, y sólo por encima de ese
 * ancho se recorta al defecto cripto.
 *
 * Distribución del catálogo global activo (`bat.assets`, `tenant_id IS NULL`,
 * `is_active`) medida el 2026-09-12, que es lo que fija el umbral en 10:
 *
 *     decimals | activos | resultado
 *     ---------+---------+---------------------------------------------
 *            0 |       4 | fiat (CLP, COP, JPY, KRW) — no pasa por aquí
 *            2 |      28 | fiat                      — no pasa por aquí
 *            6 |       6 | ADA, USDC…  → 6  (sin cambio)
 *            8 |      28 | BTC, LTC…   → 8  (sin cambio)
 *            9 |       1 | SOL         → 9  (era 8)
 *           10 |       1 | DOT         → 10 (era 8)
 *           18 |      36 | ETH, DAI…   → 8  (sin cambio, es el caso del tope)
 *
 * 10 es el techo porque es la unidad nativa MÁS ANCHA que el catálogo declara
 * fuera de los ERC-20 (el planck de Polkadot). Subirlo a 9 en vez de a 10
 * habría dejado DOT en 9 contra los 10 que la rejilla ya pinta — es decir,
 * habría reparado la incoherencia de SOL creando la misma para DOT.
 *
 * ⚠️ FIAT NO PASA POR AQUÍ. La precisión fiat la decide cada superficie por su
 * cuenta (backend: 2 fijo; frontend: 0 para las monedas sin unidad menor), y
 * esas dos también discrepan — está medido y anotado en #2234, pero es una
 * corrección de DATOS del catálogo, no de esta regla, así que esta función no
 * finge cubrirla. Sólo declara la regla CRIPTO, que es la que tenía dos copias.
 */

/**
 * Decimales de un activo cripto cuya unidad nativa no se puede enseñar entera,
 * y de cualquier ticker cuyos decimales no conocemos. Es el satoshi: el ancho
 * con el que este producto lleva enseñando cantidades desde el principio.
 */
export const DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION = 8

/**
 * El ancho máximo de decimales que una celda contable enseña ENTERO.
 *
 * Por debajo o igual, los decimales de la cadena se muestran tal cual: todos
 * los dígitos son reales y ninguno se redondea. Por encima (los ERC-20 de 18
 * decimales) se recorta a `DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION`.
 */
const WIDEST_LEGIBLE_NATIVE_UNIT_DECIMALS = 10

/**
 * Techo de cualquier precisión de display que esta regla pueda producir. Lo
 * usan las superficies que validan una precisión recibida por la API en vez de
 * derivarla: sin él, la guarda vuelve a escribir un 8 a mano y el número que
 * el backend acaba de ampliar se recorta otra vez en el cliente.
 */
export const MAX_ASSET_DISPLAY_PRECISION = WIDEST_LEGIBLE_NATIVE_UNIT_DECIMALS

/**
 * Decimales con los que se enseña un activo cripto, a partir de los decimales
 * NATIVOS de su cadena (`bat.assets.decimals`).
 *
 * @param nativeDecimals - decimales de la unidad nativa (satoshi 8, lamport 9,
 *   planck 10, lovelace 6, wei 18). Un valor que no sea un entero no negativo
 *   es dato corrupto del catálogo: se cae al defecto en vez de propagarlo a una
 *   columna de dinero.
 */
export function deriveCryptoAssetDisplayPrecision(nativeDecimals: number): number {
  if (!Number.isInteger(nativeDecimals) || nativeDecimals < 0) {
    return DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION
  }
  // La unidad nativa se enseña entera mientras quepa: ningún dígito pintado
  // aquí es inventado, porque la cadena no ofrece más allá de `nativeDecimals`.
  if (nativeDecimals <= WIDEST_LEGIBLE_NATIVE_UNIT_DECIMALS) {
    return nativeDecimals
  }
  // Por encima sólo están los ERC-20 (18 = wei): ilegibles, se recortan.
  return DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION
}
